import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Minus, Calendar } from 'lucide-react';
import { clinicianService } from '../services/clinicianService';

const ClinicianDashboardPage = () => {
  const navigate = useNavigate();
  
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Clinician Home';
  }, []);

  // State for data
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Initialize to current date - weekends now allowed
    const today = new Date();
    
    // Removed weekend skip logic - bookings now available 7 days a week
    // if (dayOfWeek === 0) { // Sunday
    //   today.setDate(today.getDate() + 1); // Move to Monday
    // } else if (dayOfWeek === 6) { // Saturday  
    //   today.setDate(today.getDate() + 2); // Move to Monday
    // }
    
    console.log('📅 Clinician Dashboard: Initial date set to:', today.toDateString(), '(Day of week:', today.getDay(), ')');
    return today;
  });
  const [expandedAppointments, setExpandedAppointments] = useState(new Set());
  
  // State for clinician info
  const [clinicianId, setClinicianId] = useState(null);
  const [clinicianInfo, setClinicianInfo] = useState(null);
  const [hasAcVitalsAccess, setHasAcVitalsAccess] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
    fetchClinicianInfo();
  }, []);

  // Fetch appointments when selected date or clinician changes
  useEffect(() => {
    if (clinicianId && selectedDate) {
      fetchAppointmentsForDate(selectedDate);
    }
  }, [clinicianId, selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use dummy data with clinical focus - in real implementation, these would be clinician-specific endpoints
      const tasksData = [
        { id: 1, text: "Review clinical assessment for Patient P002", checked: false },
        { id: 2, text: "Update treatment plan documentation", checked: false },
        { id: 3, text: "Complete certification requirements", checked: true },
        { id: 4, text: "Schedule patient follow-up calls", checked: false },
        { id: 5, text: "Review latest clinical guidelines", checked: false }
      ];

      const notificationsData = [
        { id: 1, text: "New clinical telehealth appointment scheduled", checked: false },
        { id: 2, text: "Patient assessment requires completion", checked: false },
        { id: 3, text: "Clinical documentation update available", checked: true },
        { id: 4, text: "Team meeting scheduled for next week", checked: false }
      ];

      const newsData = [
        { 
          id: 1, 
          title: "New Clinical Guidelines Released", 
          excerpt: "Updated best practices for telehealth clinical assessments and patient care protocols.",
          image: "/placeholder-news.jpg"
        },
        { 
          id: 2, 
          title: "Telehealth Platform Update", 
          excerpt: "New features for clinical monitoring, patient interaction, and treatment documentation.",
          image: "/placeholder-news.jpg"
        },
        { 
          id: 3, 
          title: "Clinical Training Program", 
          excerpt: "Upcoming certification opportunities for advanced clinical telehealth procedures.",
          image: "/placeholder-news.jpg"
        }
      ];

      setTasks(tasksData);
      setNotifications(notificationsData);
      setNews(newsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Handle authentication errors
      if (error.message.includes('Could not validate credentials') || error.message.includes('401')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClinicianInfo = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('👤 Clinician Dashboard: Current user from localStorage:', user);
      
      if (user.id) {
        const clinician = await clinicianService.getClinicianByUserId(user.id);
        console.log('👩‍⚕️ Clinician Dashboard: Clinician info retrieved:', clinician);
        setClinicianId(clinician.id);
        setClinicianInfo(clinician);
        console.log('🆔 Clinician Dashboard: Set clinician ID to:', clinician.id);
        
        // Check AC vitals access
        if (clinician.ac_vitals_access) {
          // Check if access has not expired
          if (!clinician.ac_vitals_access_expiry || new Date(clinician.ac_vitals_access_expiry) > new Date()) {
            setHasAcVitalsAccess(true);
            console.log('✅ Clinician Dashboard: AC Vitals access granted');
          } else {
            setHasAcVitalsAccess(false);
            console.log('⏰ Clinician Dashboard: AC Vitals access expired');
          }
        } else {
          setHasAcVitalsAccess(false);
          console.log('❌ Clinician Dashboard: No AC Vitals access');
        }
      } else {
        console.log('❌ Clinician Dashboard: No user ID found in localStorage');
      }
    } catch (error) {
      console.error('❌ Clinician Dashboard: Error fetching clinician info:', error);
    }
  };

  const fetchAppointmentsForDate = async (date) => {
    try {
      // Format date as YYYY-MM-DD without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      console.log('🔍 Clinician Dashboard: Fetching appointments for clinician:', clinicianId, 'date:', dateString, 'original date:', date);
      
      // Get real appointments for the specific date (like doctor's module)
      let appointmentsData = await clinicianService.getClinicianBookings(clinicianId, dateString);
      console.log('📅 Clinician Dashboard: Raw appointments data from API:', appointmentsData);
      console.log('📊 Clinician Dashboard: Number of real appointments:', appointmentsData.length);
      
      // Only display real appointments from the database
      console.log('📋 Clinician Dashboard: Total real appointments to display:', appointmentsData.length);
      
      // Sort all appointments by time
      appointmentsData.sort((a, b) => {
        const timeA = a.appointment_time || a.time; // Handle both API format (appointment_time) and dummy format (time)
        const timeB = b.appointment_time || b.time;
        return timeA.localeCompare(timeB);
      });
      console.log('📅 Clinician Dashboard: Appointments sorted by time:', appointmentsData.map(apt => ({ 
        time: apt.appointment_time || apt.time, 
        name: apt.patient_name || apt.patientName || 'Unknown Patient',
        type: apt.appointment_type,
        reason: apt.reason
      })));
      
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('❌ Clinician Dashboard: Error fetching appointments for date:', error);
      setAppointments([]);
    }
  };

  // Removed dummy appointments generator - only real data from API will be displayed

  const handleTaskToggle = async (taskId) => {
    try {
      const updatedTasks = tasks.map(task => 
        task.id === taskId ? { ...task, checked: !task.checked } : task
      );
      setTasks(updatedTasks);
      
      // In real implementation, update in backend via clinicianService
      // await clinicianService.updateTaskStatus(taskId, !task.checked);
    } catch (error) {
      console.error('Error updating task:', error);
      // Revert on error
      fetchData();
    }
  };

  const handleNotificationToggle = async (notificationId) => {
    try {
      const updatedNotifications = notifications.map(notification => 
        notification.id === notificationId ? { ...notification, checked: !notification.checked } : notification
      );
      setNotifications(updatedNotifications);
      
      // In real implementation, update in backend via clinicianService
      // await clinicianService.updateNotificationStatus(notificationId, !notification.checked);
    } catch (error) {
      console.error('Error updating notification:', error);
      // Revert on error
      fetchData();
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Reset time to compare only dates
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    
    if (compareDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (compareDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else if (compareDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
    }
  };

  const handleDateChange = (direction) => {
    setSelectedDate(prevDate => {
      const newDate = new Date(prevDate);
      
      if (direction === 'next') {
        // Move to next day - weekends now allowed
        newDate.setDate(newDate.getDate() + 1);
      } else {
        // Move to previous day - weekends now allowed
        newDate.setDate(newDate.getDate() - 1);
      }
      
      console.log('📅 Clinician Dashboard: Date changed to:', newDate.toDateString(), '(Day of week:', newDate.getDay(), ')');
      return newDate;
    });
    // Clear expanded appointments when date changes
    setExpandedAppointments(new Set());
  };

  const handleAppointmentToggle = (appointmentId) => {
    setExpandedAppointments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appointmentId)) {
        newSet.delete(appointmentId);
      } else {
        newSet.add(appointmentId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="flex gap-6">
        {/* Left Column */}
        <div className="w-[22%] space-y-2">
          {/* Tasks Section */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg text-center">
              <h3 className="font-semibold text-sm">Tasks</h3>
            </div>
            <div className="p-2 space-y-2">
              {tasks.map((task) => (
                <div key={task.id} className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    checked={task.checked}
                    onChange={() => handleTaskToggle(task.id)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">{task.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Notifications Section */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg text-center">
              <h3 className="font-semibold text-sm">Notifications</h3>
            </div>
            <div className="p-2 space-y-2">
              {notifications.map((notification) => (
                <div key={notification.id} className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    checked={notification.checked}
                    onChange={() => handleNotificationToggle(notification.id)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">{notification.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Latest News Section */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg text-center">
              <h3 className="font-semibold text-sm">Latest News</h3>
            </div>
            <div className="p-2 space-y-2">
              {news.map((item) => (
                <div key={item.id} className="flex space-x-3">
                  <div className="w-16 h-16 bg-gray-300 rounded flex-shrink-0"></div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{item.title}</h4>
                    <p className="text-xs text-gray-600 mt-1">{item.excerpt}</p>
                    <a href="#" className="text-blue-600 hover:text-blue-800 text-xs">
                      + Read More
                    </a>
                  </div>
                </div>
              ))}
              <div className="flex justify-end mt-2">
                <button 
                  onClick={() => navigate('/clinician/news')}
                  className="bg-white text-blue-600 border border-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors"
                >
                  News Page
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="w-[78%] space-y-2">
          {/* Quick Access Section */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg text-center">
              <h3 className="font-semibold text-sm">Quick Access</h3>
            </div>
            <div className="p-2">
              <div className="flex justify-center space-x-4">
                <Link to="/clinician/patientfiles" className="bg-blue-100 text-gray-700 px-6 py-2 rounded text-sm hover:bg-blue-200 transition-colors">
                  Patient Files
                </Link>
                <Link to="/clinician/accounts/rcti" className="bg-blue-100 text-gray-700 px-6 py-2 rounded text-sm hover:bg-blue-200 transition-colors">
                  Accounts
                </Link>
                <Link to="/clinician/availability/current" className="bg-blue-100 text-gray-700 px-6 py-2 rounded text-sm hover:bg-blue-200 transition-colors">
                  Availability
                </Link>
                {hasAcVitalsAccess && (
                  <Link to="/clinician/ac-vitals-monitor" className="bg-green-100 text-gray-700 px-6 py-2 rounded text-sm hover:bg-green-200 transition-colors">
                    AC Vitals Monitor
                  </Link>
                )}
                <button className="bg-blue-100 text-gray-700 px-6 py-2 rounded text-sm hover:bg-blue-200 transition-colors">
                  Messaging
                </button>
              </div>
            </div>
          </div>

          {/* Appointment/Schedule Section */}
          <div className="bg-white rounded-lg shadow-md h-[536px] flex flex-col relative">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg flex justify-center items-center">
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => handleDateChange('prev')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-1 transition-all duration-200 hover:scale-110"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <h3 className="font-semibold text-sm">{formatDate(selectedDate)}</h3>
                <button 
                  onClick={() => handleDateChange('next')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-1 transition-all duration-200 hover:scale-110"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Fixed Column Headers */}
            <div className="px-4 py-3 bg-gray-100 border-b-2 border-gray-300 shadow-sm">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex">
                  <div className="w-[8%]"></div>
                  <div className="w-[22%] text-center">
                    <span className="text-sm font-medium text-gray-700">Time</span>
                  </div>
                  <div className="w-[65%] text-center">
                    <span className="text-sm font-medium text-gray-700">Patient Name</span>
                  </div>
                  <div className="w-[5%]"></div>
                </div>
                <div className="flex">
                  <div className="w-[12%]"></div>
                  <div className="w-[18%] text-center">
                    <span className="text-sm font-medium text-gray-700">Time</span>
                  </div>
                  <div className="w-[65%] text-center">
                    <span className="text-sm font-medium text-gray-700">Patient Name</span>
                  </div>
                  <div className="w-[5%]"></div>
                </div>
              </div>
            </div>

            {/* Scrollable Appointments Content */}
            <div className="p-2 pl-4 overflow-y-auto custom-scrollbar" 
                 style={{
                   height: 'calc(100% - 140px)',
                   scrollbarWidth: 'thin',
                   scrollbarColor: '#60a5fa #e2e8f0',
                   paddingBottom: '40px'
                 }}>
              
              {/* Appointments in two independent columns */}
              {appointments.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <Calendar className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No appointments scheduled</p>
                    <p className="text-gray-400 text-sm mt-2">There are no bookings for {formatDate(selectedDate)}</p>
                  </div>
                </div>
              ) : (
                <div className="flex gap-4">
                  {/* Left Column */}
                  <div className="w-1/2 pr-3">
                    <div className="space-y-3">
                      {appointments.filter((_, index) => index % 2 === 0).map((appointment) => {
                      const isExpanded = expandedAppointments.has(appointment.id);
                      return (
                        <div key={appointment.id}>
                          {/* Main appointment row */}
                          <div className="flex items-center py-1 px-3 border-l-4 border-l-blue-400 border-b-2 border-b-gray-200 last:border-b-0 hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent hover:border-l-blue-600 transition-all duration-300 rounded-lg shadow-sm hover:shadow-md relative">
                            <div className="w-[8%]"></div>
                            <div className="w-[22%] text-center">
                              <span className="text-sm font-medium text-gray-800">{formatTime(appointment.appointment_time || appointment.time)}</span>
                            </div>
                            <div className="w-[65%] flex items-center justify-center">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-700">{appointment.patient_name || appointment.patientName}</span>
                              </div>
                              <button 
                                onClick={() => handleAppointmentToggle(appointment.id)}
                                className="text-blue-600 hover:text-blue-800 p-1 transition-colors absolute right-3"
                              >
                                {isExpanded ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                              </button>
                            </div>
                            <div className="w-[5%]"></div>
                          </div>
                          
                          {/* Expanded details */}
                          {isExpanded && (
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r mb-2">
                              <div className="space-y-2 text-sm">
                                {/* Line 1: Patient ID and Patient Name */}
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-gray-700">Patient ID: <span className="font-normal">{appointment.patient_string_id || appointment.patient_id || 'N/A'}</span></span>
                                  <span className="font-medium text-gray-700">Patient Name: <span className="font-normal">{appointment.patient_name || appointment.patientName}</span></span>
                                </div>
                                
                                {/* Line 2: Patient Phone and Patient Email */}
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-gray-700">Phone: <span className="font-normal">{appointment.patient_phone || 'N/A'}</span></span>
                                  <span className="font-medium text-gray-700">Email: <span className="font-normal">{appointment.patient_email || 'N/A'}</span></span>
                                </div>
                                
                                {/* Line 3: 4 Action Buttons */}
                                <div className="flex justify-between items-center mt-3 pt-2 border-t border-blue-200">
                                  <button className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors">
                                    Details
                                  </button>
                                  <button 
                                    onClick={() => {
                                      console.log('📂 Patient File clicked for appointment:', appointment);
                                      const patientData = {
                                        patient_id: appointment.patient_string_id || appointment.patient_id,
                                        patient_name: appointment.patient_name || `${appointment.patient_first_name || ''} ${appointment.patient_last_name || ''}`.trim(),
                                        appointment_id: appointment.id,
                                        appointment_date: appointment.appointment_date,
                                        appointment_time: appointment.appointment_time
                                      };
                                      // Use string patient ID for navigation (like "P000002")
                                      const navigationPatientId = appointment.patient_string_id || appointment.patient_id;
                                      navigate(`/clinician/patientfiles/${navigationPatientId}/general_details`, { 
                                        state: { patientData } 
                                      });
                                    }}
                                    className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors"
                                  >
                                    Patient File
                                  </button>
                                  <button className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors">
                                    Edit Booking
                                  </button>
                                  <button 
                                    onClick={() => {
                                      // Navigate to clinical telehealth notes only (no general telehealth for clinicians)
                                      console.log('🏥 Navigating to Clinical Telehealth with data:', appointment);
                                      
                                      const appointmentData = {
                                        id: appointment.id,
                                        patient_id: appointment.patient_string_id || appointment.patient_id,  // Use string ID for patient lookup
                                        patient_name: appointment.patient_name || appointment.patientName,
                                        doctor_name: appointment.doctor_name || appointment.doctor,  // Include doctor name
                                        clinician_name: appointment.clinician_name || appointment.clinician,
                                        appointment_type: 'Clinical Telehealth',
                                        appointment_date: appointment.appointment_date,
                                        appointment_time: appointment.appointment_time,
                                        reason: appointment.reason,
                                        specialty: appointment.specialty,
                                        clinic_location: appointment.clinic_location,
                                        schedule_type: appointment.schedule_type,
                                        status: appointment.status
                                      };
                                      
                                      navigate(`/clinician/clinicaltelenotes/${appointment.id}`, { state: { appointmentData } });
                                    }}
                                    className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors"
                                  >
                                    Go to Booking
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Right Column */}
                <div className="w-1/2 pl-3">
                  <div className="space-y-3">
                    {appointments.filter((_, index) => index % 2 === 1).map((appointment) => {
                      const isExpanded = expandedAppointments.has(appointment.id);
                      return (
                        <div key={appointment.id}>
                          {/* Main appointment row */}
                          <div className="flex items-center py-1 px-3 border-l-4 border-l-green-400 border-b-2 border-b-gray-200 last:border-b-0 hover:bg-gradient-to-r hover:from-green-50 hover:to-transparent hover:border-l-green-600 transition-all duration-300 rounded-lg shadow-sm hover:shadow-md relative">
                            <div className="w-[12%]"></div>
                            <div className="w-[18%] text-center">
                              <span className="text-sm font-medium text-gray-800">{formatTime(appointment.appointment_time || appointment.time)}</span>
                            </div>
                            <div className="w-[65%] flex items-center justify-center">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-700">{appointment.patient_name || appointment.patientName}</span>
                              </div>
                              <button 
                                onClick={() => handleAppointmentToggle(appointment.id)}
                                className="text-blue-600 hover:text-blue-800 p-1 transition-colors absolute right-3"
                              >
                                {isExpanded ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                              </button>
                            </div>
                            <div className="w-[5%]"></div>
                          </div>
                          
                          {/* Expanded details */}
                          {isExpanded && (
                            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r mb-2">
                              <div className="space-y-2 text-sm">
                                {/* Line 1: Patient ID and Patient Name */}
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-gray-700">Patient ID: <span className="font-normal">{appointment.patient_string_id || appointment.patient_id || 'N/A'}</span></span>
                                  <span className="font-medium text-gray-700">Patient Name: <span className="font-normal">{appointment.patient_name || appointment.patientName}</span></span>
                                </div>
                                
                                {/* Line 2: Patient Phone and Patient Email */}
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-gray-700">Phone: <span className="font-normal">{appointment.patient_phone || 'N/A'}</span></span>
                                  <span className="font-medium text-gray-700">Email: <span className="font-normal">{appointment.patient_email || 'N/A'}</span></span>
                                </div>
                                
                                {/* Line 3: 4 Action Buttons */}
                                <div className="flex justify-between items-center mt-3 pt-2 border-t border-green-200">
                                  <button className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors">
                                    Details
                                  </button>
                                  <button 
                                    onClick={() => {
                                      console.log('📂 Patient File clicked for appointment:', appointment);
                                      const patientData = {
                                        patient_id: appointment.patient_string_id || appointment.patient_id,
                                        patient_name: appointment.patient_name || `${appointment.patient_first_name || ''} ${appointment.patient_last_name || ''}`.trim(),
                                        appointment_id: appointment.id,
                                        appointment_date: appointment.appointment_date,
                                        appointment_time: appointment.appointment_time
                                      };
                                      // Use string patient ID for navigation (like "P000002")
                                      const navigationPatientId = appointment.patient_string_id || appointment.patient_id;
                                      navigate(`/clinician/patientfiles/${navigationPatientId}/general_details`, { 
                                        state: { patientData } 
                                      });
                                    }}
                                    className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors"
                                  >
                                    Patient File
                                  </button>
                                  <button className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors">
                                    Edit Booking
                                  </button>
                                  <button 
                                    onClick={() => {
                                      // Navigate to clinical telehealth notes only (no general telehealth for clinicians)
                                      console.log('🏥 Navigating to Clinical Telehealth with data:', appointment);
                                      
                                      const appointmentData = {
                                        id: appointment.id,
                                        patient_id: appointment.patient_string_id || appointment.patient_id,  // Use string ID for patient lookup
                                        patient_name: appointment.patient_name || appointment.patientName,
                                        doctor_name: appointment.doctor_name || appointment.doctor,  // Include doctor name
                                        clinician_name: appointment.clinician_name || appointment.clinician,
                                        appointment_type: 'Clinical Telehealth',
                                        appointment_date: appointment.appointment_date,
                                        appointment_time: appointment.appointment_time,
                                        reason: appointment.reason,
                                        specialty: appointment.specialty,
                                        clinic_location: appointment.clinic_location,
                                        schedule_type: appointment.schedule_type,
                                        status: appointment.status
                                      };
                                      
                                      navigate(`/clinician/clinicaltelenotes/${appointment.id}`, { state: { appointmentData } });
                                    }}
                                    className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors"
                                  >
                                    Go to Booking
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                </div>
              )}
            </div>
                
            {/* Fixed Full Calendar Button */}
            <div className="absolute bottom-4 right-4">
              <button 
                onClick={() => navigate('/clinician/calendar')}
                className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-all duration-200 hover:scale-105 flex items-center space-x-2 shadow-lg hover:shadow-xl"
              >
                <Calendar className="h-4 w-4" />
                <span>Full Calendar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicianDashboardPage;
